<template>
  <div class="relative w-fit">
    <!-- 主图底图（PNG 鼠标轮廓） -->
    <img
      v-if="imgUrl"
      :src="imgUrl"
      :width="BASE_WIDTH || undefined"
      :height="BASE_HEIGHT || undefined"
      class="block pointer-events-none select-none"
      draggable="false"
    />

    <!-- 按键覆盖层（inline SVG）：
         - 视觉层（pointer-events: none）：基色填充 + 行程点 level + 实时 trigger + 边框
         - 点击层（最上面，pointer-events: all）：透明 path，仅负责接收点击事件
         点击命中区域严格等于按键 path 形状（d 决定），不同产品用不同 SVG 文件即可。 -->
    <svg
      v-if="BASE_WIDTH && KEY_AREA_HEIGHT"
      class="absolute top-0 left-0 overflow-visible"
      :width="BASE_WIDTH"
      :height="KEY_AREA_HEIGHT"
      :viewBox="`0 0 ${BASE_WIDTH} ${KEY_AREA_HEIGHT}`"
    >
      <defs>
        <!-- 左右按键形状作为 clip，给进度填充用 -->
        <clipPath v-if="leftKeyPath" :id="`clip-left`">
          <path :d="leftKeyPath" />
        </clipPath>
        <clipPath v-if="rightKeyPath" :id="`clip-right`">
          <path :d="rightKeyPath" />
        </clipPath>

        <!-- 行程点 level 渐变：作为 mask 的灰度梯度（白=可见，黑=隐藏），实现顶部 N% 可见的"裁剪"效果 -->
        <linearGradient
          v-if="leftKeyHeight"
          :id="`level-grad-left`"
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2="0"
          :y2="leftKeyHeight"
        >
          <stop offset="0%" stop-color="#fff" />
          <stop :offset="`${leftLevelPercent}%`" stop-color="#fff" />
          <stop :offset="`${leftLevelPercent}%`" stop-color="#000" />
          <stop offset="100%" stop-color="#000" />
        </linearGradient>
        <linearGradient
          v-if="rightKeyHeight"
          :id="`level-grad-right`"
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2="0"
          :y2="rightKeyHeight"
        >
          <stop offset="0%" stop-color="#fff" />
          <stop :offset="`${rightLevelPercent}%`" stop-color="#fff" />
          <stop :offset="`${rightLevelPercent}%`" stop-color="#000" />
          <stop offset="100%" stop-color="#000" />
        </linearGradient>

        <!-- Level 遮罩：自按键顶部向下 N% 可见，其余部分隐藏 -->
        <mask
          :id="`level-mask-left`"
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          :width="BASE_WIDTH"
          :height="KEY_AREA_HEIGHT"
        >
          <rect
            x="0"
            y="0"
            :width="BASE_WIDTH"
            :height="KEY_AREA_HEIGHT"
            fill="url(#level-grad-left)"
          />
        </mask>
        <mask
          :id="`level-mask-right`"
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          :width="BASE_WIDTH"
          :height="KEY_AREA_HEIGHT"
        >
          <rect
            x="0"
            y="0"
            :width="BASE_WIDTH"
            :height="KEY_AREA_HEIGHT"
            fill="url(#level-grad-right)"
          />
        </mask>

        <!-- 行程层斜线：以竖线为基准，左键向左倾斜 30°、右键向右倾斜 30° -->
        <pattern
          :id="`level-stripes-left`"
          patternUnits="userSpaceOnUse"
          width="8"
          height="8"
          patternTransform="rotate(-30)"
        >
          <line
            x1="0"
            y1="0"
            x2="0"
            y2="8"
            :stroke="levelColor"
            stroke-width="3"
            stroke-opacity="0.6"
          />
        </pattern>
        <pattern
          :id="`level-stripes-right`"
          patternUnits="userSpaceOnUse"
          width="8"
          height="8"
          patternTransform="rotate(30)"
        >
          <line
            x1="0"
            y1="0"
            x2="0"
            y2="8"
            :stroke="levelColor"
            stroke-width="3"
            stroke-opacity="0.6"
          />
        </pattern>

        <!-- 实时 trigger 渐变 -->
        <linearGradient
          v-if="leftKeyHeight"
          :id="`trigger-left`"
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2="0"
          :y2="leftKeyHeight"
        >
          <stop offset="0%" :stop-color="triggerColor" />
          <stop :offset="`${leftTriggerPercent}%`" :stop-color="triggerColor" />
          <stop :offset="`${leftTriggerPercent}%`" stop-color="transparent" />
          <stop offset="100%" stop-color="transparent" />
        </linearGradient>
        <linearGradient
          v-if="rightKeyHeight"
          :id="`trigger-right`"
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="0"
          x2="0"
          :y2="rightKeyHeight"
        >
          <stop offset="0%" :stop-color="triggerColor" />
          <stop :offset="`${rightTriggerPercent}%`" :stop-color="triggerColor" />
          <stop :offset="`${rightTriggerPercent}%`" stop-color="transparent" />
          <stop offset="100%" stop-color="transparent" />
        </linearGradient>
      </defs>

      <!-- ============ 左键视觉层（pointer-events: none） ============ -->
      <!-- 默认底色：未选中时保持可见。 -->
      <path
        v-if="leftKeyPath && shouldShowBase('left')"
        :d="leftKeyPath"
        :fill="getBaseFill('left')"
        :fill-opacity="isCalibrationTab ? 1 : 0.2"
        pointer-events="none"
        class="transition-colors duration-200"
      />
      <!-- 悬浮/选中时叠加同色背景。 -->
      <path
        v-if="leftKeyPath && isInteractiveTab"
        :d="leftKeyPath"
        :fill="shouldShowBaseColor('left') ? baseColor : 'transparent'"
        fill-opacity="0.2"
        :opacity="shouldShowBorder('left') ? 1 : 0"
        pointer-events="none"
        class="transition-opacity duration-200"
      />
      <g v-if="leftKeyPath" :clip-path="`url(#clip-left)`" pointer-events="none">
        <rect
          v-if="isTravelTab"
          x="0"
          y="0"
          :width="BASE_WIDTH"
          :height="KEY_AREA_HEIGHT"
          :fill="levelColor"
          fill-opacity="0.3"
          mask="url(#level-mask-left)"
          :opacity="isSelectedSide('left') ? 1 : 0"
          class="transition-opacity duration-200"
        />
        <rect
          v-if="isTravelTab"
          x="0"
          y="0"
          :width="BASE_WIDTH"
          :height="KEY_AREA_HEIGHT"
          fill="url(#level-stripes-left)"
          mask="url(#level-mask-left)"
          :opacity="isSelectedSide('left') ? 1 : 0"
          class="transition-opacity duration-200"
        />
        <rect
          v-if="isTravelTab"
          x="0"
          y="0"
          :width="BASE_WIDTH"
          :height="KEY_AREA_HEIGHT"
          :fill="`url(#trigger-left)`"
          fill-opacity="0.2"
        />
        <!-- level 边界线：行程点分界，颜色/粗细与按键外框一致；level=0 时不画 -->
        <line
          v-if="isTravelTab && leftLevelPercent > 0"
          x1="0"
          :y1="leftLevelY"
          :x2="BASE_WIDTH"
          :y2="leftLevelY"
          :stroke="borderColor"
          :stroke-width="borderWidth"
          stroke-linecap="round"
          :opacity="isSelectedSide('left') ? 1 : 0"
          class="transition-opacity duration-200"
        />
      </g>
      <!-- SVG 描边默认居中；以双倍宽度描边并裁剪到按键 path 内，得到纯内边框。 -->
      <g
        v-if="leftKeyPath && isInteractiveTab"
        :clip-path="`url(#clip-left)`"
        :opacity="shouldShowBorder('left') ? 1 : 0"
        pointer-events="none"
        class="transition-opacity duration-200"
      >
        <path
          :d="leftKeyPath"
          fill="none"
          :stroke="borderColor"
          :stroke-width="borderWidth * 2"
          stroke-linejoin="round"
        />
      </g>

      <!-- ============ 右键视觉层（pointer-events: none） ============ -->
      <!-- 默认底色：未选中时保持可见。 -->
      <path
        v-if="rightKeyPath && shouldShowBase('right')"
        :d="rightKeyPath"
        :fill="getBaseFill('right')"
        :fill-opacity="isCalibrationTab ? 1 : 0.2"
        pointer-events="none"
        class="transition-colors duration-200"
      />
      <!-- 悬浮/选中时叠加同色背景。 -->
      <path
        v-if="rightKeyPath && isInteractiveTab"
        :d="rightKeyPath"
        :fill="shouldShowBaseColor('right') ? baseColor : 'transparent'"
        fill-opacity="0.2"
        :opacity="shouldShowBorder('right') ? 1 : 0"
        pointer-events="none"
        class="transition-opacity duration-200"
      />
      <g v-if="rightKeyPath" :clip-path="`url(#clip-right)`" pointer-events="none">
        <rect
          v-if="isTravelTab"
          x="0"
          y="0"
          :width="BASE_WIDTH"
          :height="KEY_AREA_HEIGHT"
          :fill="levelColor"
          fill-opacity="0.3"
          mask="url(#level-mask-right)"
          :opacity="isSelectedSide('right') ? 1 : 0"
          class="transition-opacity duration-200"
        />
        <rect
          v-if="isTravelTab"
          x="0"
          y="0"
          :width="BASE_WIDTH"
          :height="KEY_AREA_HEIGHT"
          fill="url(#level-stripes-right)"
          mask="url(#level-mask-right)"
          :opacity="isSelectedSide('right') ? 1 : 0"
          class="transition-opacity duration-200"
        />
        <rect
          v-if="isTravelTab"
          x="0"
          y="0"
          :width="BASE_WIDTH"
          :height="KEY_AREA_HEIGHT"
          :fill="`url(#trigger-right)`"
          fill-opacity="0.2"
        />
        <!-- level 边界线：行程点分界，颜色/粗细与按键外框一致；level=0 时不画 -->
        <line
          v-if="isTravelTab && rightLevelPercent > 0"
          x1="0"
          :y1="rightLevelY"
          :x2="BASE_WIDTH"
          :y2="rightLevelY"
          :stroke="borderColor"
          :stroke-width="borderWidth"
          stroke-linecap="round"
          :opacity="isSelectedSide('right') ? 1 : 0"
          class="transition-opacity duration-200"
        />
      </g>
      <!-- SVG 描边默认居中；以双倍宽度描边并裁剪到按键 path 内，得到纯内边框。 -->
      <g
        v-if="rightKeyPath && isInteractiveTab"
        :clip-path="`url(#clip-right)`"
        :opacity="shouldShowBorder('right') ? 1 : 0"
        pointer-events="none"
        class="transition-opacity duration-200"
      >
        <path
          :d="rightKeyPath"
          fill="none"
          :stroke="borderColor"
          :stroke-width="borderWidth * 2"
          stroke-linejoin="round"
        />
      </g>

      <!-- ============ 点击层（最上面，独立、透明、仅负责命中） ============ -->
      <!-- 左键点击命中 -->
      <path
        v-if="leftKeyPath && isInteractiveTab"
        :d="leftKeyPath"
        fill="transparent"
        pointer-events="all"
        class="cursor-pointer transition-opacity duration-200"
        @click="emit('select', 'left')"
        @mouseenter="hoveredSide = 'left'"
        @mouseleave="hoveredSide = null"
      />
      <!-- 右键点击命中 -->
      <path
        v-if="rightKeyPath && isInteractiveTab"
        :d="rightKeyPath"
        fill="transparent"
        pointer-events="all"
        class="cursor-pointer transition-opacity duration-200"
        @click="emit('select', 'right')"
        @mouseenter="hoveredSide = 'right'"
        @mouseleave="hoveredSide = null"
      />
    </svg>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch, unref, type ComputedRef } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useDeviceStore } from '@/stores/device.ts'
  import { getImageUrl } from '@/utils/assets'
  import type { CalibrationVisualState } from '../../types'

  /**
   * RT 鼠标布局展示组件（inline SVG 版）：
   * - 主图：rt_<color>.png（鼠标轮廓）
   * - 按键形状：运行时解析 rt_left.svg / rt_right.svg 提取 path d 和 viewBox 尺寸，
   *             作为按键区域和点击命中区域的单一来源。
   *             不同产品用不同 SVG 文件即可定义不同的按键区域，不必再把 path 写进 manifest。
   *             若 SVG 文件解析失败，则回退到 manifest 里声明的 path / height（fallback）。
   * - 视觉分层（pointer-events: none，自下而上）：
   *     1) base   —— 基色填充（带 0.8 不透明度，模拟原 SVG 的 fill-opacity）
   *     2) level  —— 行程点 level 进度（linearGradient stop offset 跟随 leftLevel / rightLevel）
   *     3) trigger —— 实时 trigger 进度
   *     4) border —— 加粗描边
   * - 点击层：放在最上面，透明 path + pointer-events="all"，命中区域 = path 形状（d 决定）。
   *           暗化时整体不可点（pointer-events-none）。
   *
   * 维度：
   *   BASE_WIDTH / BASE_HEIGHT  ← 主图 PNG 的 naturalWidth / naturalHeight
   *   KEY_AREA_HEIGHT           ← 解析 SVG 得到的两个按键 height 的较大者
   *   leftKeyHeight / rightKeyHeight ← 单个按键 SVG 的高度（用于 linearGradient y2）
   */

  type Side = 'left' | 'right'
  type Selection = Side | 'both'

  const MAX_LEVEL = 10

  const props = withDefaults(
    defineProps<{
      /** 左键配置行程点 level（1 ~ MAX_LEVEL） */
      leftLevel?: number
      /** 右键配置行程点 level（1 ~ MAX_LEVEL） */
      rightLevel?: number
      /** 左键实时行程点 level（1 ~ MAX_LEVEL） */
      leftTriggerValue?: number
      /** 右键实时行程点 level（1 ~ MAX_LEVEL） */
      rightTriggerValue?: number
      /** 基色 */
      baseColor?: string
      /** 行程点 level 填充色 */
      levelColor?: string
      /** 实时 trigger 填充色 */
      triggerColor?: string
      /** 边框颜色 */
      borderColor?: string
      /** 边框宽度（px） */
      borderWidth?: number
      /** 当前选中侧 */
      selected?: Selection
      /** 当前激活的 Tab；行程点 level 显示仅在 'travel' 时展示 */
      activeTab?: 'travel' | 'sensitivity' | 'tactile' | 'sound' | 'calibration'
      /** 校准页左右按键的基础覆盖层状态。 */
      calibrationState?: Partial<Record<Side, CalibrationVisualState>>
    }>(),
    {
      leftLevel: 0,
      rightLevel: 0,
      leftTriggerValue: 0,
      rightTriggerValue: 0,
      baseColor: '#1E44FF',
      levelColor: '#7EE7FC',
      triggerColor: '#1E44FF',
      borderColor: '#1757FF',
      borderWidth: 3,
      selected: 'both',
    },
  )

  const emit = defineEmits<{
    /** 点击按键时通知父组件，附带被点击的一侧 */
    select: [side: Side]
  }>()

  /** 加载图片读取 naturalWidth / naturalHeight（仅 PNG 主图） */
  function loadImageSize(src: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      if (!src) {
        resolve({ width: 0, height: 0 })
        return
      }
      const img = new Image()
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
      img.onerror = () => resolve({ width: 0, height: 0 })
      img.src = src
    })
  }

  function useImageSize(url: ComputedRef<string>) {
    const size = ref({ width: 0, height: 0 })
    watch(
      () => unref(url),
      async (src) => {
        size.value = await loadImageSize(src)
      },
      { immediate: true },
    )
    return size
  }

  /** SVG 解析结果：path d、宽度、高度 */
  interface SvgInfo {
    path: string
    width: number
    height: number
  }

  /**
   * 从 SVG 文件中解析 path d 和 viewBox 尺寸。
   * 取第一个 <path> 的 d；尺寸优先 width/height 属性，回退到 viewBox。
   */
  async function loadSvgInfo(url: string): Promise<SvgInfo | null> {
    if (!url) return null
    try {
      const response = await fetch(url)
      const text = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(text, 'image/svg+xml')
      const svgEl = doc.querySelector('svg')
      const pathEl = doc.querySelector('path')
      if (!svgEl || !pathEl) return null

      const d = pathEl.getAttribute('d')
      if (!d) return null

      const viewBoxAttr = svgEl.getAttribute('viewBox')?.trim()
      const viewBox = viewBoxAttr ? viewBoxAttr.split(/\s+/).map(Number) : []
      const widthAttr = parseFloat(svgEl.getAttribute('width') || '')
      const heightAttr = parseFloat(svgEl.getAttribute('height') || '')
      const width = widthAttr || viewBox[2] || 0
      const height = heightAttr || viewBox[3] || 0

      return { path: d, width, height }
    } catch {
      return null
    }
  }

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const currentDeviceColor = computed(() => {
    const colors = currentDevice.value?.manifest?.ui?.colors
    if (!colors?.series?.length) return
    return colors.series.find((color) => color.key === colors.current)
  })

  /** 底图 URL：rt_<color>.png */
  const imgUrl = computed(() => {
    const ui = currentDevice.value?.manifest?.ui
    const color = currentDeviceColor.value
    if (!ui?.assetsDir || !color?.key) return ''
    return getImageUrl(`${ui.assetsDir}/rt_${color.key}.png`)
  })

  /** 主图尺寸（PNG 的 naturalWidth / naturalHeight） */
  const mainImageSize = useImageSize(imgUrl)
  const BASE_WIDTH = computed(() => mainImageSize.value.width)
  const BASE_HEIGHT = computed(() => mainImageSize.value.height)

  /**
   * RT 覆盖层必须和 rt_<color>.png 使用同一套 266px 坐标系。
   * 普通按键页的 mouse left/right.svg 是给 634px 的 func_<color>.png 使用的，
   * 不能复用到这里，否则 path 会相对主图产生横向偏移。
   */
  const leftKeyUrl = computed(() => {
    const ui = currentDevice.value?.manifest?.ui
    if (!ui?.assetsDir) return ''
    return getImageUrl(`${ui.assetsDir}/rt_left.svg`)
  })

  const rightKeyUrl = computed(() => {
    const ui = currentDevice.value?.manifest?.ui
    if (!ui?.assetsDir) return ''
    return getImageUrl(`${ui.assetsDir}/rt_right.svg`)
  })

  /**
   * 运行时解析 SVG 文件得到的按键形状信息（path d + 尺寸）。
   * 仅从 url 指向的 .svg 文件解析；若解析失败则不渲染该侧按键。
   */
  const leftKeyInfo = ref<SvgInfo | null>(null)
  const rightKeyInfo = ref<SvgInfo | null>(null)

  watch(
    leftKeyUrl,
    async (url) => {
      leftKeyInfo.value = await loadSvgInfo(url)
    },
    { immediate: true },
  )

  watch(
    rightKeyUrl,
    async (url) => {
      rightKeyInfo.value = await loadSvgInfo(url)
    },
    { immediate: true },
  )

  /** 按键 path / 高度（供 inline SVG 使用） */
  const leftKeyPath = computed(() => leftKeyInfo.value?.path ?? '')
  const rightKeyPath = computed(() => rightKeyInfo.value?.path ?? '')
  const leftKeyHeight = computed(() => leftKeyInfo.value?.height ?? 0)
  const rightKeyHeight = computed(() => rightKeyInfo.value?.height ?? 0)

  /** 按键区域高度 = 左右按键的最大高度（保证 SVG viewBox 覆盖整个按键区） */
  const KEY_AREA_HEIGHT = computed(() => Math.max(leftKeyHeight.value, rightKeyHeight.value))

  /** level 分界线的 y 坐标（用按键 SVG 自身高度，与 linearGradient y2 同基准） */
  const leftLevelY = computed(() =>
    leftKeyHeight.value > 0 ? (leftLevelPercent.value / 100) * leftKeyHeight.value : 0,
  )
  const rightLevelY = computed(() =>
    rightKeyHeight.value > 0 ? (rightLevelPercent.value / 100) * rightKeyHeight.value : 0,
  )

  /** 0~MAX_LEVEL → 0~100，clamp 防越界 */
  function toPercent(value: number | undefined): number {
    const v = (value ?? 0) / MAX_LEVEL
    return Math.max(0, Math.min(100, v * 100))
  }

  const leftLevelPercent = computed(() => toPercent(props.leftLevel))
  const rightLevelPercent = computed(() => toPercent(props.rightLevel))
  const leftTriggerPercent = computed(() => toPercent(props.leftTriggerValue))
  const rightTriggerPercent = computed(() => toPercent(props.rightTriggerValue))

  /** 仅「按键触发行程」Tab 激活时展示行程点 level（斜线填充 + 分界线） */
  const isTravelTab = computed(() => props.activeTab === 'travel')
  const isCalibrationTab = computed(() => props.activeTab === 'calibration')
  const isInteractiveTab = computed(
    () =>
      props.activeTab === 'travel' ||
      props.activeTab === 'sensitivity' ||
      props.activeTab === 'tactile' ||
      props.activeTab === 'sound',
  )
  /** 非校准配置页保持原基础层；校准页默认不显示，触发或通过后才显示。 */
  function shouldShowBase(side: Side): boolean {
    if (isInteractiveTab.value) return true
    if (!isCalibrationTab.value) return false
    return (props.calibrationState?.[side] ?? 'idle') !== 'idle'
  }

  /** 校准页只显示基础覆盖层，并按未触发 / 已触发 / 已通过映射设计色。 */
  function getBaseFill(side: Side): string {
    if (!isCalibrationTab.value) return props.baseColor

    const state = props.calibrationState?.[side] ?? 'idle'
    if (state === 'passed') return '#17C96480'
    if (state === 'triggered') return '#1E44FF33'
    return 'transparent'
  }

  /** 当前 hover 的按键侧（用于边框可见性） */
  const hoveredSide = ref<Side | null>(null)

  /**
   * 该侧是否被选中（用于 base / level 填充的完全显示/隐藏）：
   * - selected === 'both' → 两侧都算选中
   * - selected === side → 该侧选中
   * - 其它 → 完全隐藏
   */
  function isSelectedSide(side: Side): boolean {
    if (props.selected === 'both') return true
    if (props.selected === side) return true
    return false
  }

  function shouldShowBaseColor(side: Side): boolean {
    if (props.selected === 'both') return true
    if (props.selected === side) return true
    if (hoveredSide.value === side) return false
    return false
  }

  /**
   * 边框是否应该显示：
   * - 当前被 hover → 显示
   * - 当前被选中（selected === side 或 selected === 'both'）→ 显示
   * - 其它 → 隐藏
   * 用 opacity 0/1 + transition-opacity 控制淡入淡出。
   */
  function shouldShowBorder(side: Side): boolean {
    if (hoveredSide.value === side) return true
    if (props.selected === 'both') return true
    if (props.selected === side) return true
    return false
  }
</script>

<style scoped>
  /* 视觉层与点击层完全通过 SVG 属性（pointer-events / opacity / transition）控制 */
</style>
